import { either, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCatalogContext } from 'modules/catalog/catalogSelector';
import { Catalog, CatalogQuery, SearchQuery } from 'modules/domains';
import { useQueryBuilderState } from 'modules/queryBuilder/legacyQueryBuilder/hooks';
import { useCurrentProjectId } from 'utils/hooks/useCurrentProjectId';

const appendProjectId = (projectId: number): either.Either<null, Pick<CatalogQuery, 'projectId'>> =>
  either.right({
    projectId,
  });

function appendCatalogName<T>(catalog: option.Option<Catalog>) {
  return (catalogQuery: T): either.Either<null, T & Pick<CatalogQuery, 'catalogName'>> =>
    pipe(
      catalog,
      option.map((c) => ({
        ...catalogQuery,
        catalogName: c.name,
      })),
      either.fromOption(() => null),
    );
}

function appendSearchQuery<T>(searchQuery?: SearchQuery) {
  return (catalogQuery: T): either.Either<null, T & Pick<CatalogQuery, 'searchQuery'>> =>
    pipe(
      searchQuery,
      option.fromNullable,
      option.map((sq) => ({
        ...catalogQuery,
        searchQuery: sq,
      })),
      either.fromOption(() => null),
    );
}

const createCatalogQuery = (
  projectId: number,
  catalog: option.Option<Catalog> = option.none,
  searchQuery?: SearchQuery,
): CatalogQuery | null =>
  pipe(
    projectId,
    appendProjectId,
    either.chain(appendCatalogName(catalog)),
    either.chain(appendSearchQuery(searchQuery)),
    either.toUnion,
  );

export const useGetCatalogQuery = () => {
  const projectId = useCurrentProjectId();
  const { catalog } = useCatalogContext();
  const searchQuery = useQueryBuilderState();

  return createCatalogQuery(projectId, catalog, searchQuery);
};
